/** Appointment calendar maths, formatting, and payload helpers. */

/**
 * Browser IANA timezone for display + create/update payloads.
 * Backend stores UTC; UI shows local and sends this tz on write.
 */
export function getBrowserTimeZone() {
  try {
    return (
      (typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) ||
      'UTC'
    );
  } catch {
    return 'UTC';
  }
}

/** @deprecated Use getBrowserTimeZone() — kept for older imports. */
export const APP_TIME_ZONE =
  typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    : 'UTC';

export const HOUR_HEIGHT = 64;
export const MAX_CHIPS_PER_CELL = 2;
export const SLOT_MINUTES = 15;
export const DEFAULT_DURATION_MINUTES = 30;
export const DEFAULT_START_MINUTES = 9 * 60; // 09:00
export const STATUS_META = {
  scheduled: {
    label: 'Scheduled',
    chipClass: 'bg-blue-50 text-blue-700 border-blue-200',
    barClass: 'bg-blue-500',
    eventClass: 'bg-blue-500/90 border-blue-600 text-white',
  },
  completed: {
    label: 'Completed',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    barClass: 'bg-emerald-500',
    eventClass: 'bg-emerald-500/90 border-emerald-600 text-white',
  },
  cancelled: {
    label: 'Cancelled',
    chipClass: 'bg-red-50 text-red-700 border-red-200',
    barClass: 'bg-red-500',
    eventClass: 'bg-red-500/80 border-red-600 text-white line-through',
  },
};

export const APPOINTMENT_STATUSES = ['scheduled', 'completed', 'cancelled'];

export const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const WEEKDAY_LABELS = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

export const DEFAULT_WEEKLY_SCHEDULE = WEEKDAY_KEYS.reduce(
  (acc, day, index) => {
    const isWeekend = index === 0 || index === 6;
    acc[day] = {
      type: isWeekend ? 'closed' : 'working',
      working_hours_start: '09:00',
      working_hours_end: '18:00',
    };
    return acc;
  },
  {},
);

/** Pad number to 2 digits. */
export function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Format minutes-past-midnight as HH:MM. */
export function minutesToTime(minutes) {
  const safe = Math.max(
    0,
    Math.min(24 * 60 - 1, Math.round(Number(minutes) || 0)),
  );
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

/** Parse HH:MM or HH:MM:SS into minutes-past-midnight. */
export function timeToMinutes(time) {
  if (!time || typeof time !== 'string') return null;
  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

/** Format minutes as 12h display (e.g. 9:30 AM). */
export function formatMinutesLabel(minutes) {
  const safe = Math.max(0, Math.round(Number(minutes) || 0));
  let h = Math.floor(safe / 60) % 24;
  const m = safe % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${pad2(m)} ${ampm}`;
}

/** HH:MM wall clock for a Date in the given IANA timezone. */
export function formatLocalHHMM(date, timeZone = getBrowserTimeZone()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const h = parts.find((p) => p.type === 'hour')?.value;
  const m = parts.find((p) => p.type === 'minute')?.value;
  if (h == null || m == null) return '';
  let hour = Number(h);
  if (hour === 24) hour = 0;
  return `${pad2(hour)}:${m}`;
}

/** Local display date from UTC `scheduled_at` (e.g. "26 Aug 2026"). */
export function formatScheduledAtDate(
  scheduledAt,
  timeZone = getBrowserTimeZone(),
) {
  const d = new Date(scheduledAt);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  }).format(d);
}

/** Local display time from UTC `scheduled_at` (e.g. "03:00 PM"). */
export function formatScheduledAtTime(
  scheduledAt,
  timeZone = getBrowserTimeZone(),
) {
  const d = new Date(scheduledAt);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone,
  }).format(d);
}

/** Build a Date from UTC civil date + HH:MM(/SS). */
function parseUtcWallClock(date, time) {
  if (!date || !time) return null;
  const t = String(time).trim().slice(0, 8);
  const hhmmss = t.length === 5 ? `${t}:00` : t;
  if (!/^\d{2}:\d{2}:\d{2}$/.test(hhmmss)) return null;
  const d = new Date(`${date}T${hhmmss}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Civil YYYY-MM-DD in browser local timezone for a Date (or now).
 * Uses Intl to avoid DST/local-offset drift for day keys.
 */
export function toDayKey(date = new Date(), timeZone = getBrowserTimeZone()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  if (!y || !m || !day) return null;
  return `${y}-${m}-${day}`;
}

/** Parse YYYY-MM-DD into a Date at UTC midnight (stable day math). */
export function dayKeyToUtcDate(dayKey) {
  if (!dayKey || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null;
  const [y, m, d] = dayKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDaysToKey(dayKey, days) {
  const base = dayKeyToUtcDate(dayKey);
  if (!base) return null;
  base.setUTCDate(base.getUTCDate() + days);
  return `${base.getUTCFullYear()}-${pad2(base.getUTCMonth() + 1)}-${pad2(base.getUTCDate())}`;
}

export function addMonthsToKey(dayKey, months) {
  const base = dayKeyToUtcDate(dayKey);
  if (!base) return null;
  const day = base.getUTCDate();
  base.setUTCDate(1);
  base.setUTCMonth(base.getUTCMonth() + months);
  const lastDay = new Date(
    Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0),
  ).getUTCDate();
  base.setUTCDate(Math.min(day, lastDay));
  return `${base.getUTCFullYear()}-${pad2(base.getUTCMonth() + 1)}-${pad2(base.getUTCDate())}`;
}

/** Sunday-start week containing dayKey. */
export function startOfWeekKey(dayKey) {
  const base = dayKeyToUtcDate(dayKey);
  if (!base) return null;
  const dow = base.getUTCDay(); // 0 = Sunday
  return addDaysToKey(dayKey, -dow);
}

export function getWeekdayKey(dayKey) {
  const base = dayKeyToUtcDate(dayKey);
  if (!base) return null;
  return WEEKDAY_KEYS[base.getUTCDay()];
}

/** 42-cell month grid starting Sunday of the week that contains the 1st. */
export function buildMonthGrid(anchorKey) {
  const base = dayKeyToUtcDate(anchorKey);
  if (!base) return [];
  const firstOfMonth = `${base.getUTCFullYear()}-${pad2(base.getUTCMonth() + 1)}-01`;
  const start = startOfWeekKey(firstOfMonth);
  const month = base.getUTCMonth();
  const year = base.getUTCFullYear();
  const todayKey = toDayKey(new Date());

  return Array.from({ length: 42 }, (_, i) => {
    const key = addDaysToKey(start, i);
    const d = dayKeyToUtcDate(key);
    return {
      key,
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month && d.getUTCFullYear() === year,
      isToday: key === todayKey,
      weekdayKey: WEEKDAY_KEYS[d.getUTCDay()],
    };
  });
}

/** 7-day week grid starting Sunday. */
export function buildWeekGrid(anchorKey) {
  const start = startOfWeekKey(anchorKey);
  const todayKey = toDayKey(new Date());
  return Array.from({ length: 7 }, (_, i) => {
    const key = addDaysToKey(start, i);
    const d = dayKeyToUtcDate(key);
    return {
      key,
      day: d.getUTCDate(),
      isToday: key === todayKey,
      weekdayKey: WEEKDAY_KEYS[d.getUTCDay()],
      label: WEEKDAY_LABELS[WEEKDAY_KEYS[d.getUTCDay()]],
    };
  });
}

export function formatMonthTitle(anchorKey) {
  const d = dayKeyToUtcDate(anchorKey);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export function formatWeekTitle(anchorKey) {
  const days = buildWeekGrid(anchorKey);
  if (!days.length) return '';
  const first = dayKeyToUtcDate(days[0].key);
  const last = dayKeyToUtcDate(days[6].key);
  const sameMonth = first.getUTCMonth() === last.getUTCMonth();
  const left = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(first);
  const right = new Intl.DateTimeFormat('en-US', {
    month: sameMonth ? undefined : 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(last);
  return `${left} – ${right}`;
}

export function formatDayTitle(dayKey) {
  const d = dayKeyToUtcDate(dayKey);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/** Snap minutes to SLOT_MINUTES. */
export function snapMinutes(minutes, step = SLOT_MINUTES) {
  return Math.round(Number(minutes) / step) * step;
}

/**
 * Ensure a clicked slot is in the future (browser local timezone).
 * Returns { dayKey, startMinutes } or null if the day is fully past.
 */
export function clampSlotToFuture(dayKey, minutes, now = new Date()) {
  const tz = getBrowserTimeZone();
  const todayKey = toDayKey(now, tz);
  if (!dayKey || !todayKey) return null;
  if (dayKey < todayKey) return null;

  let startMinutes = snapMinutes(
    minutes == null ? DEFAULT_START_MINUTES : minutes,
  );
  if (dayKey === todayKey) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const h = Number(parts.find((p) => p.type === 'hour')?.value || 0);
    const m = Number(parts.find((p) => p.type === 'minute')?.value || 0);
    const nowMinutes = h * 60 + m;
    const ceilNext =
      nowMinutes % SLOT_MINUTES === 0
        ? nowMinutes + SLOT_MINUTES
        : Math.ceil(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES;

    if (minutes != null) {
      if (startMinutes < nowMinutes) {
        return null;
      }
    } else {
      startMinutes = Math.max(startMinutes, ceilNext);
    }

    if (startMinutes >= 24 * 60) return null;
  }

  return { dayKey, startMinutes };
}

export function readAnyContactName(source) {
  if (!source) return '';
  if (typeof source === 'string') return source.trim();
  const candidates = [
    source.full_name,
    source.name,
    source.display_name,
    source.contact_name,
    [source.first_name, source.last_name].filter(Boolean).join(' '),
    [source.contact_first_name, source.contact_last_name]
      .filter(Boolean)
      .join(' '),
    source.contact?.full_name,
    source.contact?.name,
    [source.contact?.first_name, source.contact?.last_name]
      .filter(Boolean)
      .join(' '),
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim()) return c.trim();
  }
  return '';
}

/**
 * Civil date + start/end in browser local timezone for display and edit.
 * Source of truth: `scheduled_at` (UTC). Same path for Flow + Manual.
 * Duration from UTC `end_time` (or start/end pair) when present.
 */
export function getAppointmentScheduleFields(appointment) {
  const timezone = appointment?.timezone || getBrowserTimeZone();
  if (!appointment) {
    return {
      date: '',
      start_time: '',
      end_time: '',
      timezone,
      durationMinutes: DEFAULT_DURATION_MINUTES,
    };
  }

  // API date/start_time/end_time are civil times in `timezone`, not UTC.
  const civilDate = String(appointment.date || '').slice(0, 10);
  const civilStart = String(appointment.start_time || '').slice(0, 5);
  const civilEnd = String(appointment.end_time || '').slice(0, 5);
  if (
    /^\d{4}-\d{2}-\d{2}$/.test(civilDate) &&
    /^\d{2}:\d{2}$/.test(civilStart) &&
    /^\d{2}:\d{2}$/.test(civilEnd)
  ) {
    const startMins = timeToMinutes(civilStart);
    const endMins = timeToMinutes(civilEnd);
    const durationMinutes =
      startMins != null && endMins != null
        ? endMins > startMins
          ? endMins - startMins
          : endMins + 24 * 60 - startMins
        : DEFAULT_DURATION_MINUTES;
    return {
      date: civilDate,
      start_time: civilStart,
      end_time: civilEnd,
      timezone,
      durationMinutes,
    };
  }

  const scheduledRaw = appointment.scheduled_at || appointment.start;
  let startDate =
    scheduledRaw != null && scheduledRaw !== '' ? new Date(scheduledRaw) : null;
  if (startDate && Number.isNaN(startDate.getTime())) startDate = null;

  // Fallback: backend UTC civil fields when scheduled_at missing
  if (!startDate) {
    const utcDate =
      appointment.date ||
      appointment.appointment_date ||
      (typeof appointment.start === 'string' && appointment.start.includes('T')
        ? appointment.start.slice(0, 10)
        : '');
    const utcStart =
      appointment.start_time ||
      appointment.startTime ||
      (typeof appointment.start === 'string' && appointment.start.includes('T')
        ? appointment.start.slice(11, 16)
        : '');
    startDate = parseUtcWallClock(utcDate, utcStart);
  }

  let durationMinutes = DEFAULT_DURATION_MINUTES;
  const utcDate =
    appointment.date || (startDate ? toDayKey(startDate, 'UTC') : '') || '';
  const utcEnd = appointment.end_time || appointment.endTime || '';
  if (startDate && utcDate && utcEnd) {
    let endDate = parseUtcWallClock(utcDate, utcEnd);
    if (endDate && endDate <= startDate) {
      const nextDay = addDaysToKey(utcDate, 1);
      endDate = parseUtcWallClock(nextDay, utcEnd);
    }
    if (endDate && endDate > startDate) {
      durationMinutes = Math.round((endDate - startDate) / 60000);
    }
  } else {
    const s = timeToMinutes(String(appointment.start_time || '').slice(0, 5));
    const e = timeToMinutes(String(appointment.end_time || '').slice(0, 5));
    if (s != null && e != null) {
      durationMinutes = e > s ? e - s : e + 24 * 60 - s;
    }
  }

  if (!startDate) {
    return {
      date: '',
      start_time: '',
      end_time: '',
      timezone,
      durationMinutes: DEFAULT_DURATION_MINUTES,
    };
  }

  const date = toDayKey(startDate, timezone);
  const start_time = formatLocalHHMM(startDate, timezone);
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  const end_time = formatLocalHHMM(endDate, timezone);

  return {
    date: date || '',
    start_time,
    end_time,
    timezone,
    durationMinutes,
  };
}

/** Normalize API appointment into a calendar event (local wall clock). */
export function toCalendarEvent(appointment) {
  const schedule = getAppointmentScheduleFields(appointment);
  const startMinutes = timeToMinutes(schedule.start_time) ?? 0;
  const duration =
    schedule.durationMinutes > 0
      ? schedule.durationMinutes
      : DEFAULT_DURATION_MINUTES;
  let endMinutes = timeToMinutes(schedule.end_time);
  if (endMinutes == null || endMinutes <= startMinutes) {
    endMinutes = startMinutes + duration;
  }

  const contactName =
    readAnyContactName(appointment.contact) ||
    readAnyContactName(appointment) ||
    appointment.contact_name ||
    'Appointment';

  const assigneeName =
    appointment.assigned_employee?.full_name ||
    appointment.assigned_employee?.name ||
    appointment.assigned_employee_name ||
    '';

  return {
    id: appointment.id,
    dayKey: schedule.date,
    startMinutes,
    endMinutes,
    status: appointment.status || 'scheduled',
    title: contactName,
    contactName,
    assigneeName,
    contactId: appointment.contact_id || appointment.contact?.id || null,
    assignedEmployeeId:
      appointment.assigned_employee_id ||
      appointment.assigned_employee?.id ||
      null,
    timezone: schedule.timezone,
    scheduledAt: appointment.scheduled_at || null,
    flowName: appointment.flow_name ?? null,
    googleMeetLink:
      typeof appointment.google_meet_link === 'string'
        ? appointment.google_meet_link.trim()
        : '',
    raw: appointment,
  };
}

/**
 * Google-Calendar-style overlap layout.
 * Returns events with lane + laneCount.
 */
export function layoutDayEvents(events = []) {
  const sorted = [...events].sort((a, b) => {
    if (a.startMinutes !== b.startMinutes) {
      return a.startMinutes - b.startMinutes;
    }
    return b.endMinutes - a.endMinutes;
  });

  const clusters = [];
  let active = [];
  let clusterEnd = -1;

  for (const event of sorted) {
    if (active.length && event.startMinutes >= clusterEnd) {
      clusters.push(active);
      active = [];
      clusterEnd = -1;
    }
    active.push(event);
    clusterEnd = Math.max(clusterEnd, event.endMinutes);
  }
  if (active.length) clusters.push(active);

  const laidOut = [];
  for (const cluster of clusters) {
    const laneEnds = [];
    const withLanes = cluster.map((event) => {
      let lane = laneEnds.findIndex((end) => end <= event.startMinutes);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(event.endMinutes);
      } else {
        laneEnds[lane] = event.endMinutes;
      }
      return { ...event, lane };
    });
    const laneCount = laneEnds.length || 1;
    for (const item of withLanes) {
      laidOut.push({ ...item, laneCount });
    }
  }

  return laidOut;
}

/**
 * Normalize weekly_schedule from API into a day-keyed map for the UI.
 * Accepts:
 * - array: [{ day_of_week, status, working_hours_start, working_hours_end }, ...]
 * - object: { monday: { type|status, ... }, ... }
 * - JSON string of either
 */
export function normalizeWeeklySchedule(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  const base = WEEKDAY_KEYS.reduce((acc, day, index) => {
    const isWeekend = index === 0 || index === 6;
    acc[day] = {
      type: isWeekend ? 'closed' : 'working',
      working_hours_start: '09:00',
      working_hours_end: '18:00',
    };
    return acc;
  }, {});

  if (Array.isArray(parsed)) {
    for (const entry of parsed) {
      const idx = Number(entry?.day_of_week);
      if (!Number.isInteger(idx) || idx < 0 || idx > 6) continue;
      const day = WEEKDAY_KEYS[idx];
      const status = entry.status || entry.type || 'closed';
      base[day] = {
        type: status,
        working_hours_start:
          status === 'closed'
            ? null
            : String(entry.working_hours_start || '09:00').slice(0, 5),
        working_hours_end:
          status === 'closed'
            ? null
            : String(entry.working_hours_end || '18:00').slice(0, 5),
      };
    }
    return base;
  }

  if (parsed && typeof parsed === 'object') {
    for (const day of WEEKDAY_KEYS) {
      const cfg = parsed[day];
      if (!cfg) continue;
      const status = cfg.status || cfg.type || 'closed';
      base[day] = {
        type: status,
        working_hours_start:
          status === 'closed'
            ? null
            : String(cfg.working_hours_start || '09:00').slice(0, 5),
        working_hours_end:
          status === 'closed'
            ? null
            : String(cfg.working_hours_end || '18:00').slice(0, 5),
      };
    }
  }

  return base;
}

/** Read holiday reason/title from API or UI shapes. */
export function getHolidayReason(h) {
  if (!h || typeof h !== 'object') return '';
  const value = h.reason ?? h.name ?? h.title ?? h.label ?? '';
  return typeof value === 'string' ? value.trim() : String(value || '').trim();
}

/** Holiday is active only when it has a non-empty date and title/reason. */
export function isValidHoliday(h) {
  return Boolean(h?.date && getHolidayReason(h));
}

/** Keep holidays that have a valid title/reason. */
export function getValidHolidays(holidays = []) {
  if (!Array.isArray(holidays)) return [];
  return holidays.filter(isValidHoliday);
}

export function getHolidayTypeLabel(type) {
  switch (type) {
    case 'half_day':
      return 'Half Day';
    case 'custom_hours':
      return 'Custom Hours';
    case 'extra_working_day':
      return 'Extra Working Day';
    case 'full_day_off':
    default:
      return 'Holiday';
  }
}

export function formatHolidayDisplayDate(dayKey) {
  const d = dayKeyToUtcDate(dayKey);
  if (!d) return dayKey || '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

/**
 * Normalize holidays from API into UI shape.
 * Accepts array or JSON string; maps reason/name/title to both name + reason.
 */
export function normalizeHolidays(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = [];
    }
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.map((h) => {
    const reason = getHolidayReason(h);
    const type = h.exception_type || h.type || 'full_day_off';
    return {
      date: h.date || '',
      name: reason,
      reason,
      type,
      working_hours_start: h.working_hours_start || '09:00',
      working_hours_end: h.working_hours_end || '18:00',
    };
  });
}

export const REMINDER_OFFSET_PRESETS = [
  { label: '5 minutes before', value: 5 },
  { label: '15 minutes before', value: 15 },
  { label: '30 minutes before', value: 30 },
  { label: '1 hour before', value: 60 },
  { label: '2 hours before', value: 120 },
  { label: '1 day (24 hours) before', value: 1440 },
  { label: '2 days (48 hours) before', value: 2880 },
];

export function formatReminderOffset(minutes) {
  const m = Number(minutes);
  if (isNaN(m) || m <= 0) return '0 minutes before';
  if (m % 1440 === 0) {
    const days = m / 1440;
    return `${days} ${days === 1 ? 'day' : 'days'} before`;
  }
  if (m % 60 === 0) {
    const hours = m / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} before`;
  }
  return `${m} minutes before`;
}

/** Normalize reminder settings from API into UI state */
export function normalizeReminderSettings(reminders) {
  if (!Array.isArray(reminders) || reminders.length === 0) {
    return [
      {
        id: 'rem_default_1',
        offset_minutes: 60,
        is_enabled: true,
        channels: ['whatsapp'],
        template_name: '',
        template_language: 'en_US',
        template_variables: {},
        message_template:
          'Hi {{name}}, reminder for your appointment on {{date}} at {{time}} with {{employee}}.',
        email_subject: 'Reminder: Your appointment with {{company}}',
        email_body_template:
          'Hi {{name}},\n\nThis is a reminder for your upcoming appointment on {{datetime}} with {{employee}}.\nJoin here: {{meet_link}}',
        meet_link_template_name: '',
        meet_link_template_language: 'en_US',
        meet_link_template_variables: {},
      },
    ];
  }

  // Group incoming API items by offset_minutes
  const groupedByOffset = {};
  reminders.forEach((r, index) => {
    const offset = Number(r.offset_minutes) || 15;
    if (!groupedByOffset[offset]) {
      groupedByOffset[offset] = {
        id: r.id ? String(r.id) : `rem_${offset}_${index + 1}`,
        offset_minutes: offset,
        is_enabled: r.is_enabled !== false,
        channels: [],
        template_name: '',
        template_language: 'en_US',
        template_variables: {},
        message_template:
          'Hi {{name}}, reminder for your appointment on {{date}} at {{time}} with {{employee}}.',
        email_subject: 'Reminder: Your appointment with {{company}}',
        email_body_template:
          'Hi {{name}},\n\nThis is a reminder for your upcoming appointment on {{datetime}} with {{employee}}.\nJoin here: {{meet_link}}',
        meet_link_template_name: '',
        meet_link_template_language: 'en_US',
        meet_link_template_variables: {},
      };
    }

    const item = groupedByOffset[offset];
    const itemChannels =
      Array.isArray(r.channels) && r.channels.length > 0
        ? r.channels
        : [r.channel || 'whatsapp'];

    itemChannels.forEach((ch) => {
      if (!item.channels.includes(ch)) {
        item.channels.push(ch);
      }
    });

    if (r.is_enabled) item.is_enabled = true;

    if (
      r.channel === 'whatsapp' ||
      itemChannels.includes('whatsapp') ||
      r.template_name
    ) {
      if (r.template_name) item.template_name = r.template_name;
      if (r.template_language) item.template_language = r.template_language;
      if (r.template_variables && typeof r.template_variables === 'object') {
        item.template_variables = r.template_variables;
      }
      if (r.message_template) item.message_template = r.message_template;
      if (r.meet_link_template_name) {
        item.meet_link_template_name = r.meet_link_template_name;
      }
      if (r.meet_link_template_language) {
        item.meet_link_template_language = r.meet_link_template_language;
      }
      if (
        r.meet_link_template_variables &&
        typeof r.meet_link_template_variables === 'object'
      ) {
        item.meet_link_template_variables = r.meet_link_template_variables;
      }
    }

    if (
      r.channel === 'email' ||
      itemChannels.includes('email') ||
      r.email_subject ||
      r.email_body_template
    ) {
      if (r.email_subject) item.email_subject = r.email_subject;
      if (r.email_body_template)
        item.email_body_template = r.email_body_template;
    }
  });

  return Object.values(groupedByOffset).map((item) => ({
    ...item,
    channels: item.channels.length > 0 ? item.channels : ['whatsapp'],
  }));
}

/** Clean reminder settings before sending to API (expands channels array into separate API rule objects) */
export function cleanReminderSettingsForApi(reminders) {
  if (!Array.isArray(reminders)) return [];
  const cleaned = [];

  reminders
    .filter(
      (r) =>
        r &&
        typeof r.offset_minutes === 'number' &&
        !isNaN(r.offset_minutes) &&
        r.offset_minutes > 0,
    )
    .forEach((r) => {
      const channels = Array.isArray(r.channels)
        ? r.channels
        : [r.channel || 'whatsapp'];

      if (channels.includes('whatsapp')) {
        cleaned.push({
          ...(r.id ? { id: String(r.id) } : {}),
          offset_minutes: Number(r.offset_minutes),
          is_enabled: Boolean(r.is_enabled !== false),
          channel: 'whatsapp',
          channels: channels,
          template_name: (r.template_name || '').trim(),
          template_language: (r.template_language || 'en_US').trim(),
          template_variables: r.template_variables || {},
          message_template: (r.message_template || '').trim(),
          meet_link_template_name: (r.meet_link_template_name || '').trim(),
          meet_link_template_language: (
            r.meet_link_template_language || 'en_US'
          ).trim(),
          meet_link_template_variables: r.meet_link_template_variables || {},
        });
      }

      if (channels.includes('email')) {
        cleaned.push({
          ...(r.id ? { id: String(r.id) } : {}),
          offset_minutes: Number(r.offset_minutes),
          is_enabled: Boolean(r.is_enabled !== false),
          channel: 'email',
          channels: channels,
          email_subject: (r.email_subject || '').trim(),
          email_body_template: (r.email_body_template || '').trim(),
        });
      }
    });

  return cleaned;
}

/** Validate reminder settings before saving */
export function validateReminderSettings(reminders) {
  if (!Array.isArray(reminders)) return null;
  for (let i = 0; i < reminders.length; i++) {
    const r = reminders[i];
    if (r.is_enabled !== false) {
      const channels = Array.isArray(r.channels)
        ? r.channels
        : [r.channel || 'whatsapp'];

      if (channels.length === 0) {
        return `Select at least one channel (WhatsApp or Email) for reminder #${i + 1} (${formatReminderOffset(r.offset_minutes)}).`;
      }

      if (channels.includes('whatsapp')) {
        if (!r.template_name || !r.template_name.trim()) {
          return `WhatsApp Meta Template is required for reminder #${i + 1} (${formatReminderOffset(r.offset_minutes)}).`;
        }
        if ((r.meet_link_template_name || '').trim()) {
          const mapped = Object.values(r.meet_link_template_variables || {});
          if (!mapped.includes('{{meet_link}}')) {
            return 'Meet link template must map a variable to {{meet_link}}.';
          }
        }
      }

      if (channels.includes('email')) {
        if (!r.email_subject || !r.email_subject.trim()) {
          return `Email Subject is required for reminder #${i + 1} (${formatReminderOffset(r.offset_minutes)}).`;
        }
        if (!r.email_body_template || !r.email_body_template.trim()) {
          return `Email Body template is required for reminder #${i + 1} (${formatReminderOffset(r.offset_minutes)}).`;
        }
      }
    }
  }
  return null;
}

/** Normalize full appointment settings payload from API. */
export function normalizeAppointmentSettings(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  return {
    ...raw,
    weekly_schedule: normalizeWeeklySchedule(raw.weekly_schedule),
    holidays: getValidHolidays(normalizeHolidays(raw.holidays)),
    reminder_settings: normalizeReminderSettings(raw.reminder_settings),
  };
}

/**
 * Resolve availability for a civil day from settings.
 * Returns { available, type, startMinutes, endMinutes, label }.
 */
export function getDayAvailability(dayKey, settings) {
  if (!dayKey) {
    return {
      available: false,
      type: 'closed',
      startMinutes: null,
      endMinutes: null,
      label: 'Unavailable',
    };
  }

  const weekday = getWeekdayKey(dayKey);
  const weekly = normalizeWeeklySchedule(settings?.weekly_schedule);
  const holidays = Array.isArray(settings?.holidays) ? settings.holidays : [];
  const holiday = holidays.find((h) => h?.date === dayKey);

  if (holiday && isValidHoliday(holiday)) {
    const hType = holiday.exception_type || holiday.type;
    const hName = getHolidayReason(holiday);
    if (hType === 'full_day_off') {
      return {
        available: false,
        type: 'full_day_off',
        startMinutes: null,
        endMinutes: null,
        label: hName,
      };
    }
    if (
      hType === 'half_day' ||
      hType === 'custom_hours' ||
      hType === 'extra_working_day'
    ) {
      const startMinutes = timeToMinutes(
        holiday.working_hours_start || '09:00',
      );
      const endMinutes = timeToMinutes(holiday.working_hours_end || '18:00');
      const resolvedType = hType === 'half_day' ? 'half_day' : hType;
      return {
        available: true,
        type: resolvedType,
        startMinutes,
        endMinutes,
        label:
          getHolidayReason(holiday) ||
          (hType === 'extra_working_day'
            ? 'Extra working day'
            : hType === 'half_day'
              ? 'Half day'
              : 'Custom hours'),
      };
    }
  }

  const dayCfg = weekly?.[weekday] || DEFAULT_WEEKLY_SCHEDULE[weekday];
  if (!dayCfg || dayCfg.type === 'closed') {
    return {
      available: false,
      type: 'closed',
      startMinutes: null,
      endMinutes: null,
      label: 'Closed',
    };
  }

  return {
    available: true,
    type: dayCfg.type || 'working',
    startMinutes: timeToMinutes(dayCfg.working_hours_start || '09:00'),
    endMinutes: timeToMinutes(dayCfg.working_hours_end || '18:00'),
    label: dayCfg.type === 'half_day' ? 'Half day' : 'Working',
  };
}

export function validateAppointmentForm(values) {
  const errors = {};
  const contactId = Number(values.contact_id);
  const employeeId = Number(values.assigned_employee_id);

  if (!contactId || contactId <= 0) errors.contact_id = 'Contact is required';
  if (!employeeId || employeeId <= 0) {
    errors.assigned_employee_id = 'Assigned employee is required';
  }
  if (!values.date) errors.date = 'Date is required';
  if (!values.start_time) errors.start_time = 'Start time is required';
  if (!values.end_time) errors.end_time = 'End time is required';

  const start = timeToMinutes(values.start_time);
  const end = timeToMinutes(values.end_time);
  if (start != null && end != null && end <= start) {
    errors.end_time = 'End time must be after start time';
  }

  return errors;
}

export const GOOGLE_CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar';

function meetLinkValue(source) {
  const link = source?.google_meet_link;
  return typeof link === 'string' ? link.trim() : '';
}

/** Include create_meet_link only when the user opted in and no link exists yet. */
export function withCreateMeetLink(payload, values, existing) {
  if (!values?.create_meet_link) return payload;
  if (meetLinkValue(existing)) return payload;
  return { ...payload, create_meet_link: true };
}

/** Send local civil date/times + browser timezone — backend converts to UTC. */
export function buildCreatePayload(values) {
  return withCreateMeetLink(
    {
      contact_id: Number(values.contact_id),
      assigned_employee_id: Number(values.assigned_employee_id),
      date: values.date,
      start_time: values.start_time?.slice(0, 5),
      end_time: values.end_time?.slice(0, 5),
      timezone: getBrowserTimeZone(),
      status: 'scheduled',
      also_assign_lead: !!values.also_assign_lead,
    },
    values,
  );
}

export function buildUpdatePayload(values, existing) {
  const browserTz = getBrowserTimeZone();
  const nextLocal = {
    contact_id: Number(values.contact_id),
    assigned_employee_id: Number(values.assigned_employee_id),
    date: values.date,
    start_time: values.start_time?.slice(0, 5),
    end_time: values.end_time?.slice(0, 5),
    timezone: browserTz,
    status: values.status || existing?.status || 'scheduled',
    also_assign_lead: !!values.also_assign_lead,
  };

  if (!existing) {
    return nextLocal;
  }

  const prevLocal = {
    contact_id: Number(existing.contact_id || existing.contact?.id),
    assigned_employee_id: Number(
      existing.assigned_employee_id || existing.assigned_employee?.id,
    ),
    ...getAppointmentScheduleFields(existing),
    status: existing.status || 'scheduled',
    also_assign_lead: !!existing.also_assign_lead,
  };

  const payload = {};
  let scheduleChanged = false;

  for (const key of Object.keys(nextLocal)) {
    if (String(nextLocal[key] ?? '') !== String(prevLocal[key] ?? '')) {
      if (['date', 'start_time', 'end_time'].includes(key)) {
        scheduleChanged = true;
      } else if (key !== 'timezone') {
        payload[key] = nextLocal[key];
      }
    }
  }

  if (scheduleChanged) {
    payload.date = nextLocal.date;
    payload.start_time = nextLocal.start_time;
    payload.end_time = nextLocal.end_time;
    payload.timezone = browserTz;
  }

  return withCreateMeetLink(payload, values, existing);
}

/**
 * Build PATCH payload weekly_schedule as backend array:
 * [{ day_of_week, status, working_hours_start, working_hours_end }, ...]
 * day_of_week: 0=Sunday … 6=Saturday (matches JS Date.getDay / WEEKDAY_KEYS).
 */
export function cleanWeeklyScheduleForApi(weeklySchedule) {
  return WEEKDAY_KEYS.map((day, dayOfWeek) => {
    const cfg = weeklySchedule?.[day] || DEFAULT_WEEKLY_SCHEDULE[day];
    const status = cfg.status || cfg.type || 'closed';
    return {
      day_of_week: dayOfWeek,
      status,
      working_hours_start:
        status === 'closed'
          ? null
          : String(cfg.working_hours_start || '09:00').slice(0, 5),
      working_hours_end:
        status === 'closed'
          ? null
          : String(cfg.working_hours_end || '18:00').slice(0, 5),
    };
  });
}

export function cleanHolidaysForApi(holidays = []) {
  const seen = new Set();
  const cleaned = [];
  for (const h of holidays) {
    if (!h?.date || seen.has(h.date) || !isValidHoliday(h)) continue;
    seen.add(h.date);
    const type = h.type || h.exception_type || 'full_day_off';
    cleaned.push({
      date: h.date,
      reason: getHolidayReason(h),
      exception_type: type,
      working_hours_start:
        type === 'full_day_off'
          ? null
          : (h.working_hours_start || '09:00').slice(0, 5),
      working_hours_end:
        type === 'full_day_off'
          ? null
          : (h.working_hours_end || '18:00').slice(0, 5),
    });
  }
  return cleaned;
}

export function validateSettingsForm(weeklySchedule, holidays) {
  const errors = {};
  for (const day of WEEKDAY_KEYS) {
    const cfg = weeklySchedule?.[day];
    if (!cfg || cfg.type === 'closed') continue;
    const start = timeToMinutes(cfg.working_hours_start);
    const end = timeToMinutes(cfg.working_hours_end);
    if (start == null || end == null || end <= start) {
      errors[`weekly.${day}`] = `${WEEKDAY_LABELS[day]} hours are invalid`;
    }
  }

  const dates = (holidays || []).map((h) => h.date).filter(Boolean);
  const unique = new Set(dates);
  if (unique.size !== dates.length) {
    errors.holidays = 'Holiday dates must be unique';
  }

  for (const h of holidays || []) {
    if (!h?.date) {
      errors.holidays = 'Each holiday needs a date';
      break;
    }
    if (h.type !== 'full_day_off') {
      const start = timeToMinutes(h.working_hours_start);
      const end = timeToMinutes(h.working_hours_end);
      if (start == null || end == null || end <= start) {
        errors.holidays = `Invalid hours for ${h.date}`;
        break;
      }
    }
  }

  return errors;
}

export function groupEventsByDay(events = []) {
  const byDay = events.reduce((acc, event) => {
    if (!event?.dayKey) return acc;
    if (!acc[event.dayKey]) acc[event.dayKey] = [];
    acc[event.dayKey].push(event);
    return acc;
  }, {});

  for (const dayKey of Object.keys(byDay)) {
    byDay[dayKey].sort((a, b) => {
      if (a.startMinutes !== b.startMinutes) {
        return a.startMinutes - b.startMinutes;
      }
      return (a.endMinutes ?? 0) - (b.endMinutes ?? 0);
    });
  }

  return byDay;
}

export function countByStatus(events = []) {
  return events.reduce(
    (acc, e) => {
      const s = e.status || 'scheduled';
      acc[s] = (acc[s] || 0) + 1;
      acc.total += 1;
      return acc;
    },
    { scheduled: 0, completed: 0, cancelled: 0, total: 0 },
  );
}

/** Build HH:MM options every `step` minutes. */
export function buildTimeOptions(step = SLOT_MINUTES) {
  const options = [];
  for (let m = 0; m < 24 * 60; m += step) {
    options.push({ value: minutesToTime(m), label: formatMinutesLabel(m) });
  }
  return options;
}
